import Image from 'next/image';
import React from 'react';


interface FeatureVisualProps {
  src: string;
  alt: string;
  aspectRatio?: '16/9' | '21/9' | '4/3' | '1/1';
  className?: string;
  caption?: string;
}

const aspectRatioStyles = {
  '16/9': 'aspect-video',
  '21/9': 'aspect-[21/9]',
  '4/3': 'aspect-[4/3]',
  '1/1': 'aspect-square',
};

export function FeatureVisual({
  src,
  alt,
  aspectRatio = '16/9',
  className = '',
  caption,
}: FeatureVisualProps) {
  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative bg-neutral-100 rounded-xl border-2 border-neutral-200 overflow-hidden ${aspectRatioStyles[aspectRatio]}`}
      >
        <Image src={src} alt={alt} fill className="object-cover" unoptimized />
      </div>
      {caption && <p className="text-sm text-neutral-500 mt-3 text-center">{caption}</p>}
    </div>
  );
}

interface ComparisonVisualProps {
  beforeImage: string;
  beforeLabel: string;
  afterImage: string;
  afterLabel: string;
  beforeBadges?: string[];
  afterBadges?: string[];
}

export function ComparisonVisual({
  beforeImage,
  beforeLabel,
  afterImage,
  afterLabel,
  beforeBadges = [],
  afterBadges = [],
}: ComparisonVisualProps) {
  return (
    <div className="space-y-8">
      <div className="relative bg-neutral-50 rounded-xl border-2 border-neutral-200 overflow-hidden">
        <div className="absolute top-4 left-4 z-10 bg-red-500 text-white px-3 py-1.5 text-sm font-bold rounded-lg">
          {beforeLabel}
        </div>
        <div className="relative aspect-[16/10] md:aspect-video">
          <Image
            src={beforeImage}
            alt={beforeLabel}
            fill
            className="object-cover grayscale"
            unoptimized
          />
        </div>
        {beforeBadges.length > 0 && (
          <div className="p-4 bg-neutral-100 flex flex-wrap gap-2 justify-center border-t border-neutral-200">
            {beforeBadges.map((badge, i) => (
              <span
                key={i}
                className="text-sm bg-red-50 text-red-700 px-3 py-1.5 rounded-full font-medium"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <div className="bg-neutral-900 text-white font-bold text-xl w-14 h-14 rounded-full flex items-center justify-center shadow-lg">
          VS
        </div>
      </div>

      <div className="relative bg-white rounded-xl border-2 border-green-400 overflow-hidden shadow-[4px_4px_0px_0px_rgba(34,197,94,0.3)]">
        <div className="absolute top-4 right-4 z-10 bg-green-500 text-white px-3 py-1.5 text-sm font-bold rounded-lg">
          {afterLabel}
        </div>
        <div className="relative aspect-[16/10] md:aspect-video">
          <Image src={afterImage} alt={afterLabel} fill className="object-cover" unoptimized />
        </div>
        {afterBadges.length > 0 && (
          <div className="p-4 bg-green-50 flex flex-wrap gap-2 justify-center border-t border-green-200">
            {afterBadges.map((badge, i) => (
              <span
                key={i}
                className="text-sm bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-medium"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
